// ui.jsx — shared UI components for Giant Valley JM.
// Styling via CSS classes defined in styles.css.
import React, { useState, useEffect, useRef } from 'react';
import { Icon } from './Icon.jsx';
import { STATUS, COMPANY, TODAY } from '../lib/format.js';

// ── Button ──────────────────────────────────────────────────
function Btn({ variant = 'primary', size, full, icon, children, onClick, disabled, style, type }) {
  const cls = ['gv-btn', `is-${variant}`];
  if (size) cls.push(`is-${size}`);
  if (full) cls.push('is-full');
  return (
    <button type={type || 'button'} className={cls.join(' ')} onClick={onClick} disabled={disabled} style={style}>
      {icon && <Icon name={icon} size={size === 'sm' ? 17 : 19} stroke={2.1} />}
      {children && <span>{children}</span>}
    </button>);

}

// ── Status badge ────────────────────────────────────────────
function StatusBadge({ status, dot, size }) {
  const s = STATUS[status] || STATUS.planerad;
  return (
    <span className={'gv-badge' + (size === 'sm' ? ' is-sm' : '')}
    style={{ color: s.color, background: s.wash }}>
      {dot !== false && <span className="gv-badge-dot" style={{ background: s.color }} />}
      {s.label}
    </span>);

}

// ── Section label ───────────────────────────────────────────
function SecLabel({ children, action, onAction }) {
  return (
    <div className="gv-seclabel">
      <span>{children}</span>
      {action && <button className="gv-seclabel-act" onClick={onAction}>{action}</button>}
    </div>);

}

// ── Card ────────────────────────────────────────────────────
function Card({ children, onClick, className, style, pad }) {
  return (
    <div className={'gv-card' + (onClick ? ' is-tap' : '') + (className ? ' ' + className : '')}
    onClick={onClick} style={{ padding: pad, ...style }}>
      {children}
    </div>);

}

// ── Avatar ──────────────────────────────────────────────────
function Avatar({ name, size = 40 }) {
  const initials = (name || '').split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="gv-avatar" style={{ width: size, height: size, fontSize: size * 0.36 }}>{initials}</div>);

}

// ── App header (custom, sits below status bar) ─────────────
function AppHeader({ title, subtitle, onBack, action, large, right }) {
  return (
    <div className={'gv-header' + (large ? ' is-large' : '')}>
      <div className="gv-header-row">
        {onBack &&
        <button className="gv-iconbtn" onClick={onBack} aria-label="Tillbaka">
            <Icon name="back" size={22} stroke={2.2} />
          </button>
        }
        <div className="gv-header-tt">
          {subtitle && <div className="gv-header-sub">{subtitle}</div>}
          <div className="gv-header-title">{title}</div>
        </div>
        {right || action &&
        <button className="gv-iconbtn" onClick={action.onClick} aria-label={action.label}>
            <Icon name={action.icon} size={22} stroke={2.1} />
          </button>
        }
      </div>
    </div>);

}

// ── Bottom tab bar + FAB ────────────────────────────────────
function TabBar({ active, onTab, onCreate }) {
  const tabs = [
  { key: 'today', label: 'Idag', icon: 'today' },
  { key: 'calendar', label: 'Kalender', icon: 'calendar' },
  { key: 'invoice', label: 'Fakturering', icon: 'invoice' },
  { key: 'lediga', label: 'Lediga tider', icon: 'clock' }];

  return (
    <div className="gv-tabbar">
      <div className="gv-tabbar-inner">
        {tabs.slice(0, 2).map((t) =>
        <TabItem key={t.key} t={t} active={active === t.key} onClick={() => onTab(t.key)} />
        )}
        <button className="gv-fab" onClick={onCreate} aria-label="Nytt uppdrag">
          <Icon name="plus" size={28} stroke={2.4} />
        </button>
        {tabs.slice(2).map((t) =>
        <TabItem key={t.key} t={t} active={active === t.key} onClick={() => onTab(t.key)} />
        )}
      </div>
    </div>);

}
function TabItem({ t, active, onClick }) {
  return (
    <button className={'gv-tab' + (active ? ' is-active' : '')} onClick={onClick}>
      <Icon name={t.icon} size={23} stroke={active ? 2.3 : 1.9} />
      <span>{t.label}</span>
    </button>);

}

// ── Number stepper ──────────────────────────────────────────
function Stepper({ value, onChange, step = 1, min = 0, suffix, size }) {
  const [txt, setTxt] = useState(String(value));
  useEffect(() => {setTxt(fmtNumInput(value));}, [value]);
  const commit = (v) => {
    let n = parseFloat(String(v).replace(',', '.'));
    if (isNaN(n)) n = min;
    n = Math.max(min, Math.round(n * 100) / 100);
    onChange(n);
  };
  return (
    <div className={'gv-step' + (size === 'sm' ? ' is-sm' : '')}>
      <button className="gv-step-btn" onClick={() => commit((value || 0) - step)} aria-label="Minska">
        <Icon name="minus" size={16} stroke={2.4} />
      </button>
      <input className="gv-step-val" inputMode="decimal" value={txt}
      onChange={(e) => setTxt(e.target.value)}
      onBlur={() => commit(txt)}
      onFocus={(e) => e.target.select()} />
      <button className="gv-step-btn" onClick={() => commit((value || 0) + step)} aria-label="Öka">
        <Icon name="plus" size={16} stroke={2.4} />
      </button>
    </div>);

}
function fmtNumInput(n) {
  if (n == null) return '0';
  return String(n).replace('.', ',');
}

// ── Text field ──────────────────────────────────────────────
function Field({ label, value, onChange, placeholder, multiline, type, suffix, prefix, mono, readOnly, onFocus, onBlur }) {
  return (
    <label className="gv-field">
      {label && <span className="gv-label">{label}</span>}
      <div className={'gv-input-wrap' + (readOnly ? ' is-read' : '')}>
        {prefix && <span className="gv-affix">{prefix}</span>}
        {multiline ?
        <textarea className="gv-input" rows={3} value={value} placeholder={placeholder}
        onChange={(e) => onChange && onChange(e.target.value)} readOnly={readOnly} onFocus={onFocus} onBlur={onBlur} /> :

        <input className={'gv-input' + (mono ? ' is-mono' : '')} type={type || 'text'} value={value}
        placeholder={placeholder} onChange={(e) => onChange && onChange(e.target.value)}
        readOnly={readOnly} onFocus={onFocus} onBlur={onBlur} />
        }
        {suffix && <span className="gv-affix">{suffix}</span>}
      </div>
    </label>);

}

// ── Bottom sheet ────────────────────────────────────────────
function Sheet({ open, onClose, title, children, maxH }) {
  const [mounted, setMounted] = useState(open);
  const [show, setShow] = useState(open);
  useEffect(() => {
    if (open) {setMounted(true);setShow(true);} else
    {setShow(false);const t = setTimeout(() => setMounted(false), 280);return () => clearTimeout(t);}
  }, [open]);
  if (!mounted) return null;
  return (
    <div className={'gv-sheet-backdrop' + (show ? ' is-open' : '')} onClick={onClose}>
      <div className={'gv-sheet' + (show ? ' is-open' : '')} style={{ maxHeight: maxH || '82%' }}
      onClick={(e) => e.stopPropagation()}>
        <div className="gv-sheet-grab" />
        {title &&
        <div className="gv-sheet-head">
            <span>{title}</span>
            <button className="gv-iconbtn is-soft" onClick={onClose}><Icon name="x" size={20} stroke={2.2} /></button>
          </div>
        }
        <div className="gv-sheet-body">{children}</div>
      </div>
    </div>);

}

// ── Static map mock ─────────────────────────────────────────
// Schematic, muted map with road grid, optional A→B route and a pin.
function MapMock({ pin = { x: 0.5, y: 0.45 }, route, height = 200, label, compact }) {
  // deterministic schematic roads
  const roadsH = [0.22, 0.45, 0.68, 0.86];
  const roadsV = [0.18, 0.4, 0.62, 0.82];
  const start = { x: 0.16, y: 0.84 };
  const W = 400,H = 240;
  const px = (v) => v * W,py = (v) => v * H;
  return (
    <div className="gv-map" style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid slice" style={{ width: '100%', height: '100%', display: 'block' }}>
        <rect width={W} height={H} fill="#e9ede9" data-comment-anchor="5ffbea4cf4-rect-204-9" />
        {/* park / water blocks */}
        <rect x={px(0.62)} y={py(0)} width={px(0.4)} height={py(0.22)} fill="#dde7dc" />
        <rect x={px(0)} y={py(0.45)} width={px(0.18)} height={py(0.55)} fill="#d7e3ea" />
        <rect x={px(0.4)} y={py(0.68)} width={px(0.42)} height={py(0.18)} fill="#dde7dc" />
        {/* blocks subtle */}
        {roadsH.map((y, i) => <rect key={'bh' + i} x={0} y={py(y) - 1} width={W} height={2.5} fill="#fff" opacity="0.9" />)}
        {roadsV.map((x, i) => <rect key={'bv' + i} x={px(x) - 1} y={0} width={2.5} height={H} fill="#fff" opacity="0.9" />)}
        {/* main diagonal road */}
        <path d={`M0 ${py(0.95)} L ${px(0.62)} ${py(0.4)} L ${W} ${py(0.28)}`} stroke="#fff" strokeWidth="5" fill="none" />
        {/* route */}
        {route &&
        <g>
            <path d={`M${px(start.x)} ${py(start.y)} L ${px(start.x)} ${py(0.45)} L ${px(pin.x)} ${py(0.45)} L ${px(pin.x)} ${py(pin.y)}`}
          stroke="#c05e35" strokeWidth="4.5" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="0.1 0" />
            <circle cx={px(start.x)} cy={py(start.y)} r="6" fill="#fff" stroke="#c05e35" strokeWidth="3" />
          </g>
        }
      </svg>
      {/* pin marker */}
      <div className="gv-map-pin" style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}>
        <div className="gv-map-pin-dot" />
        <div className="gv-map-pin-stem" />
      </div>
      {label && <div className="gv-map-label">{label}</div>}
      <div className="gv-map-grain" />
    </div>);

}

// ── Calendar (date picker sheet) ────────────────────────────
const CAL_MONTHS = ['Januari', 'Februari', 'Mars', 'April', 'Maj', 'Juni', 'Juli', 'Augusti', 'September', 'Oktober', 'November', 'December'];
const CAL_WD = ['Mån', 'Tis', 'Ons', 'Tor', 'Fre', 'Lör', 'Sön'];
function CalendarSheet({ open, onClose, value, onSelect, minISO = TODAY }) {
  const sel = value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : TODAY;
  const [view, setView] = useState(() => {const d = new Date(sel + 'T00:00:00');return { y: d.getFullYear(), m: d.getMonth() };});
  useEffect(() => {if (open) {const d = new Date(sel + 'T00:00:00');setView({ y: d.getFullYear(), m: d.getMonth() });}}, [open]);

  const first = new Date(view.y, view.m, 1);
  const startIdx = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const iso = (d) => `${view.y}-${String(view.m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
  const cells = [];
  for (let i = 0; i < startIdx; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const prev = () => setView((v) => v.m === 0 ? { y: v.y - 1, m: 11 } : { y: v.y, m: v.m - 1 });
  const next = () => setView((v) => v.m === 11 ? { y: v.y + 1, m: 0 } : { y: v.y, m: v.m + 1 });

  return (
    <Sheet open={open} onClose={onClose} title="Välj datum">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <button className="gv-iconbtn is-soft" onClick={prev} aria-label="Föregående månad"><Icon name="chevL" size={20} stroke={2.2} /></button>
        <div style={{ fontSize: 16, fontWeight: 740 }}>{CAL_MONTHS[view.m]} {view.y}</div>
        <button className="gv-iconbtn is-soft" onClick={next} aria-label="Nästa månad"><Icon name="chevR" size={20} stroke={2.2} /></button>
      </div>
      <div className="gv-cal-grid" style={{ marginBottom: 2 }}>
        {CAL_WD.map((w) => <div key={w} className="gv-cal-wd">{w}</div>)}
      </div>
      <div className="gv-cal-grid" style={{ paddingBottom: 8 }}>
        {cells.map((d, i) => {
          if (d == null) return <div key={'e' + i} />;
          const di = iso(d);
          const isSel = di === sel;
          const isToday = di === TODAY;
          const disabled = minISO && di < minISO;
          return (
            <button key={d} className={'gv-cal-day' + (isSel ? ' is-sel' : '') + (isToday && !isSel ? ' is-today' : '') + (disabled ? ' is-disabled' : '')}
            onClick={() => {onSelect(di);onClose();}}>{d}</button>);

        })}
      </div>
    </Sheet>);

}

// ── Toggle switch ───────────────────────────────────────────
function Toggle({ on, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} onClick={() => onChange(!on)}
    style={{ width: 46, height: 28, borderRadius: 999, border: 'none', background: on ? 'var(--accent)' : 'var(--line-2)', position: 'relative', transition: 'background .2s', flexShrink: 0, cursor: 'pointer', padding: 0 }}>
      <span style={{ position: 'absolute', top: 3, left: on ? 21 : 3, width: 22, height: 22, borderRadius: '50%', background: '#fff', transition: 'left .2s', boxShadow: '0 1px 3px rgba(0,0,0,.28)' }} />
    </button>);

}

// ── Profile avatar button (header, top-right) ───────────────
function ProfileButton({ onClick }) {
  return (
    <button type="button" onClick={onClick} aria-label="Profil"
    style={{ border: 'none', background: 'none', padding: 0, borderRadius: '50%', flexShrink: 0, cursor: 'pointer' }}>
      <Avatar name={COMPANY.user} size={40} />
    </button>);

}

export {
  Btn, StatusBadge, SecLabel, Card, Avatar, AppHeader, TabBar, TabItem,
  Stepper, Field, Sheet, MapMock, CalendarSheet, Toggle, ProfileButton
};